import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, MessageSquare } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { fmtDateOnly, fmtTime12 } from "../admin/leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = {
  Confirmed: "bg-green-600/20 text-green-400 border-green-500/40",
  "Pending Confirmation": "bg-amber-600/20 text-amber-400 border-amber-500/40",
  "Reschedule Requested": "bg-orange-600/20 text-orange-400 border-orange-500/40",
  "Cancellation Requested": "bg-red-600/20 text-red-400 border-red-500/40",
};

export const PortalAppointments = () => {
  const [appts, setAppts] = useState([]);
  const [msgs, setMsgs] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/appointments`, { withCredentials: true });
      setAppts(res.data);
    } catch { /* none */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const respond = async (a, action, message) => {
    try {
      await axios.post(`${API}/portal/appointments/${a.appointment_id}/respond`, { action, message }, { withCredentials: true });
      toast.success(action === "confirm" ? "Appointment confirmed — see you then!" : "Sent! We'll follow up shortly.");
      setMsgs((m) => ({ ...m, [a.appointment_id]: "" }));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    }
  };

  if (appts.length === 0) return null;

  return (
    <div className="bg-[#121212] border border-blue-500/30 rounded-xl p-8" data-testid="portal-appointments-card">
      <div className="flex items-center gap-3 mb-5">
        <CalendarCheck className="h-5 w-5 text-blue-500" />
        <h3 className="font-heading text-lg font-semibold text-white">Upcoming Appointments</h3>
      </div>
      <div className="space-y-4">
        {appts.map((a) => {
          const msg = msgs[a.appointment_id] || "";
          return (
          <div key={a.appointment_id} data-testid={`portal-appt-${a.appointment_id}`} className="border border-zinc-800 rounded-sm p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <div className="text-white font-semibold">{fmtDateOnly(a.date)} at {fmtTime12(a.start_time)}</div>
                <div className="text-zinc-400 text-sm">{a.service_type}{a.technician_name ? ` · Tech: ${a.technician_name}` : ""}</div>
              </div>
              <span className={`text-xs font-bold uppercase border rounded-sm px-2 py-1 ${statusColor[a.status] || "bg-blue-600/20 text-blue-300 border-blue-500/40"}`} data-testid={`portal-appt-status-${a.appointment_id}`}>
                {a.status}
              </span>
            </div>
            {a.status !== "Confirmed" && (
              <div className="flex flex-wrap gap-2 mb-3">
                <button data-testid={`portal-confirm-${a.appointment_id}`} onClick={() => respond(a, "confirm")} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white rounded-sm px-4 py-2 text-xs font-semibold">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Confirm Appointment
                </button>
                <button data-testid={`portal-change-${a.appointment_id}`} onClick={() => respond(a, "change", msg)} className="border border-zinc-700 text-white rounded-sm px-4 py-2 text-xs font-semibold">Request Change</button>
                <button data-testid={`portal-cancel-${a.appointment_id}`} onClick={() => respond(a, "cancel_request", msg)} className="border border-red-500/50 text-red-400 rounded-sm px-4 py-2 text-xs font-semibold">Request Cancellation</button>
              </div>
            )}
            <div className="flex gap-2">
              <input
                data-testid={`portal-message-input-${a.appointment_id}`}
                className="flex-1 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 h-10 text-sm placeholder:text-zinc-600"
                placeholder="Message us about this appointment..."
                value={msg}
                onChange={(e) => setMsgs((m) => ({ ...m, [a.appointment_id]: e.target.value }))}
              />
              <button data-testid={`portal-message-send-${a.appointment_id}`} onClick={() => msg && respond(a, "message", msg)} className="shrink-0 inline-flex items-center gap-1.5 bg-blue-600 text-white rounded-sm px-4 text-xs font-semibold">
                <MessageSquare className="h-3.5 w-3.5" /> Send
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

const PREF_CATEGORIES = [
  ["booking", "Booking confirmations"], ["reminders", "Appointment reminders"],
  ["schedule_changes", "Schedule changes"], ["tech_updates", "Technician status updates"],
  ["estimates", "Estimates"], ["payments", "Invoices & payments"],
  ["completion", "Service completion"], ["maintenance", "Maintenance reminders"],
  ["messages", "Messages"],
];

export const NotificationSettings = ({ user }) => {
  const [prefs, setPrefs] = useState(user.notification_prefs || {});
  const [promo, setPromo] = useState(Boolean(user.promotional_consent));

  const save = async (nextPrefs, nextPromo) => {
    try {
      await axios.put(`${API}/portal/notification-settings`, { prefs: nextPrefs, promotional_consent: nextPromo }, { withCredentials: true });
      toast.success("Notification settings saved");
    } catch {
      toast.error("Could not save settings");
    }
  };

  const toggle = (key, channel) => {
    const cur = prefs[key] || {};
    const next = { ...prefs, [key]: { ...cur, [channel]: !(cur[channel] ?? true) } };
    setPrefs(next);
    save(next, promo);
  };

  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-xl p-8" data-testid="portal-notification-settings">
      <h3 className="font-heading text-lg font-semibold text-white mb-4">Notification Settings</h3>
      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-2 text-sm items-center">
        <div /><div className="text-zinc-500 text-xs">In-app</div><div className="text-zinc-500 text-xs">Push</div><div className="text-zinc-500 text-xs">Email</div>
        {PREF_CATEGORIES.map(([key, label]) => {
          const p = prefs[key] || {};
          return [
            <div key={`${key}-l`} className="text-zinc-300">{label}</div>,
            <Checkbox key={`${key}-a`} data-testid={`pref-${key}-in_app`} checked={p.in_app ?? true} onCheckedChange={() => toggle(key, "in_app")} />,
            <Checkbox key={`${key}-p`} data-testid={`pref-${key}-push`} checked={p.push ?? true} onCheckedChange={() => toggle(key, "push")} />,
            <Checkbox key={`${key}-e`} data-testid={`pref-${key}-email`} checked={p.email ?? true} onCheckedChange={() => toggle(key, "email")} />,
          ];
        })}
      </div>
      <label className="flex items-start gap-2 text-xs text-zinc-400 mt-5 cursor-pointer border-t border-zinc-800 pt-4">
        <Checkbox data-testid="pref-promotional" checked={promo} onCheckedChange={(v) => { setPromo(v); save(prefs, v); }} className="mt-0.5" />
        I agree to receive promotional messages and exclusive offers (optional — separate from service messages)
      </label>
      <p className="text-zinc-600 text-[11px] mt-3">Required operational messages (e.g. cancellations or major schedule changes) may still be sent when necessary. Reply STOP to any SMS to opt out.</p>
    </div>
  );
};
